import React from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, Users, BarChart3, Target, CheckCircle2, MapPin, MessageCircle, ArrowRight,
  Lightbulb, Settings, Search, Trophy, Star, School,
} from "lucide-react";
import ManagerProfileCard from "@/components/app/ManagerProfileCard";

const IMG = (f) => `/landing/pmm/${f}`;
const GOLD = { backgroundImage: "linear-gradient(180deg,#fff3b0,#f5c542 45%,#c98a0b)", WebkitBackgroundClip: "text", backgroundClip: "text" };

const FEATURES = [
  { icon: BookOpen, ms: "Aktiviti Pembelajaran Menarik", en: "Engaging Learning Activities", c: "from-orange-500 to-orange-700" },
  { icon: Users, ms: "Suasana Kelas Yang Menyeronokkan", en: "A Fun Class Atmosphere", c: "from-navy-600 to-navy-800" },
  { icon: BarChart3, ms: "Guru Berpengalaman & Profesional", en: "Experienced & Professional Teachers", c: "from-orange-500 to-orange-700" },
  { icon: Target, ms: "Fokus Pada Kemahiran Membaca", en: "Focus On Reading Skills", c: "from-navy-600 to-navy-800" },
];
const WILL = [
  { ms: "Mengenal huruf dengan mudah", en: "Recognise letters easily" },
  { ms: "Teknik mengeja yang berkesan", en: "Effective spelling technique" },
  { ms: "Latihan membaca bertahap", en: "Step-by-step reading practice" },
  { ms: "Lebih fokus dan yakin", en: "More focused and confident" },
  { ms: "Aktiviti interaktif dan menyeronokkan", en: "Interactive and fun activities" },
  { ms: "Sesuai untuk umur 4 - 9 tahun", en: "Suitable for ages 4 - 9" },
];
const NOTE = [
  { ms: "Suku Kata", en: "Syllables" }, { ms: "Mengeja", en: "Spelling" }, { ms: "Membaca", en: "Reading" },
  { ms: "Menulis", en: "Writing" }, { ms: "Lebih Yakin", en: "More Confident" },
];
const PROCESS = [
  { icon: Lightbulb, ms: "Rancang", en: "Plan" }, { icon: Settings, ms: "Laksana", en: "Execute" },
  { icon: Search, ms: "Pantau", en: "Monitor" }, { icon: BarChart3, ms: "Optimize", en: "Optimise" },
  { icon: Trophy, ms: "Capai Hasil", en: "Achieve" },
];

function ManagerBlock({ title, poster, alt, manager, setLightbox, testId }) {
  return (
    <div>
      <p className="text-center font-display text-xs font-bold uppercase tracking-widest text-orange-600">{title}</p>
      <div className="mt-2 overflow-hidden rounded-2xl">
        <button type="button" onClick={() => setLightbox(poster)} className="block w-full" data-testid={`${testId}-poster`}>
          <img src={poster} alt={alt} className="h-auto w-full transition-transform duration-500 hover:scale-105" loading="lazy" />
        </button>
      </div>
      <a href={manager.wa} target="_blank" rel="noopener noreferrer" className="mt-3 flex items-center justify-center gap-2 rounded-full bg-orange-500 px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-lg transition-colors hover:bg-orange-600" data-testid={`${testId}-call-btn`}>
        <MessageCircle className="h-5 w-5" /> {`WhatsApp ${manager.name} ${manager.phoneDisplay}`}
      </a>
    </div>
  );
}

export default function FizikalClassCard({ ms, cms, setLightbox, cardRef, top, managers, managerPoster, sabahPoster, gallery, mainImg, branches }) {
  const L = (m, e) => (ms ? m : e);
  const photos = [cms.physical_image || mainImg, ...gallery];
  return (
    <div ref={cardRef} className="pmm-pop min-w-0 overflow-hidden rounded-[2rem] border-4 border-orange-500 bg-white shadow-2xl shadow-orange-500/20 transition-shadow duration-300 hover:shadow-orange-500/40 md:sticky" style={{ animationDelay: "0.1s", top: `${top}px` }} data-testid="pilihan-card-fizikal">
      {/* ===== Header ===== */}
      <div className="relative overflow-hidden bg-gradient-to-br from-navy-900 via-navy-800 to-navy-600 px-6 pb-8 pt-7 text-center sm:px-8">
        <div className="pointer-events-none absolute -left-16 -top-16 h-48 w-48 rounded-full bg-orange-500/30 blur-3xl" />
        <div className="pointer-events-none absolute -right-12 bottom-0 h-40 w-40 rounded-full bg-yellow-400/20 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-600 via-yellow-300 to-orange-600" />
        <span className="relative inline-block rounded-full bg-orange-500 px-6 py-1.5 font-display text-sm font-bold uppercase tracking-widest text-white shadow-md">{L("Kelas", "Class")}</span>
        <p className="relative mt-3 font-display text-[11px] font-extrabold uppercase tracking-[0.25em] text-yellow-300">Program Membaca</p>
        <h3 className="relative font-display text-6xl font-extrabold uppercase leading-none tracking-tight text-transparent drop-shadow sm:text-7xl" style={GOLD}>PMM</h3>
        <div className="relative mx-auto mt-3 inline-block -rotate-1 rounded-lg border-2 border-yellow-400 bg-gradient-to-r from-orange-700 via-orange-500 to-orange-700 px-5 py-2 shadow-xl shadow-orange-700/40">
          <span className="font-display text-xl font-extrabold uppercase tracking-tight text-white sm:text-2xl">{L("Kelas Fizikal Bersemuka", "Face-To-Face Class")}</span>
        </div>
        <div className="relative mx-auto mt-3 inline-block rounded-full bg-gradient-to-r from-yellow-200 to-yellow-400 px-5 py-1 font-display text-xs font-extrabold uppercase text-navy-950 shadow sm:text-sm">
          {L("Khas Untuk Umur 4 - 9 Tahun", "Specially For Ages 4 - 9")}
        </div>
        <p className="relative mt-3 font-display text-2xl font-extrabold uppercase italic tracking-tight text-white sm:text-3xl">
          {L("\u201CMesti Lancar Membaca\u201D", "\u201CMust Read Fluently\u201D")}
        </p>
        <p className="relative mt-1 font-script text-xl text-yellow-200">{L("Membaca Hari Ini, Masa Depan Lebih Cerah!", "Read Today, A Brighter Tomorrow!")}</p>
      </div>

      <div className="space-y-7 p-6 sm:p-8">
        {/* ===== Hero group photo ===== */}
        <div className="relative pb-4" data-testid="fizikal-hero-visual">
          <div className="pmm-cinematic overflow-hidden rounded-2xl border-4 border-yellow-400 shadow-2xl">
            <button type="button" onClick={() => setLightbox(IMG("poster-fizikal.jpg"))} className="block w-full" data-testid="fizikal-main-img">
              <img src={IMG("fizikal-group.jpg")} alt={L("Murid kelas fizikal PMM bersama modul", "PMM physical class students with modules")} className="h-auto w-full transition-transform duration-700 hover:scale-105" loading="lazy" />
            </button>
          </div>
          <div className="pmm-pop absolute -left-2 -top-5 grid h-20 w-20 -rotate-6 place-items-center rounded-full border-4 border-yellow-400 bg-gradient-to-br from-orange-600 to-orange-800 p-1.5 sm:h-24 sm:w-24 sm:p-2 text-center shadow-xl sm:h-28 sm:w-28">
            <span className="font-display text-[9px] font-extrabold uppercase leading-tight text-white sm:text-[11px]">
              {L("Anak", "Kids")}<br /><span className="text-yellow-300">{L("Lebih Fokus", "More Focus")}</span><br /><span className="text-yellow-300">{L("Lebih Yakin", "More Confident")}</span><br /><span className="text-yellow-300">{L("Lebih Cerdas", "Smarter")}</span>
            </span>
          </div>
          <div className="absolute -bottom-6 -right-2 w-28 rotate-3 rounded-lg bg-white p-1.5 sm:-bottom-3 sm:right-2 sm:w-36 sm:p-2 shadow-2xl ring-1 ring-navy-100" data-testid="fizikal-notepad">
            <div className="mb-1 flex justify-center gap-1">{[0, 1, 2, 3, 4].map((i) => <span key={i} className="h-1.5 w-1.5 rounded-full bg-navy-300" />)}</div>
            {NOTE.map((n) => (
              <div key={n.en} className="flex items-center gap-1.5 text-[10px] font-bold text-navy-900 sm:text-xs">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 fill-orange-600 text-white" /> {ms ? n.ms : n.en}
              </div>
            ))}
          </div>
        </div>
        <p className="text-center font-script text-2xl text-navy-800">{L("Setiap Anak Boleh Membaca!", "Every Child Can Read!")}</p>

        {/* ===== 4 round features ===== */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="fizikal-features">
          {FEATURES.map((f, i) => (
            <div key={f.en} className="pmm-pop flex flex-col items-center gap-2 text-center" style={{ animationDelay: `${0.1 + i * 0.08}s` }}>
              <span className={`grid h-16 w-16 place-items-center rounded-full border-4 border-white bg-gradient-to-br ${f.c} text-white shadow-lg ring-2 ring-yellow-400 transition-transform duration-300 hover:scale-110`}>
                <f.icon className="h-7 w-7" />
              </span>
              <span className="font-display text-[10px] font-extrabold uppercase leading-tight text-navy-900 sm:text-[11px]">{ms ? f.ms : f.en}</span>
            </div>
          ))}
        </div>

        {/* ===== Anak anda akan + Modul ===== */}
        <div className="grid gap-4 sm:grid-cols-5">
          <div className="overflow-hidden rounded-2xl border-2 border-yellow-400 bg-white shadow-lg sm:col-span-3" data-testid="fizikal-will">
            <div className="bg-gradient-to-r from-yellow-300 to-yellow-500 px-4 py-2 font-display text-sm font-extrabold uppercase tracking-wide text-navy-950">{L("Anak Anda Akan:", "Your Child Will:")}</div>
            <ul className="space-y-1.5 p-4">
              {WILL.map((w) => (
                <li key={w.en} className="flex items-start gap-2 text-xs font-semibold text-navy-900 sm:text-sm">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 fill-orange-600 text-white" /> {ms ? w.ms : w.en}
                </li>
              ))}
            </ul>
          </div>
          <div className="overflow-hidden rounded-2xl bg-gradient-to-b from-orange-600 to-orange-800 p-1 shadow-lg sm:col-span-2" data-testid="fizikal-modul">
            <p className="py-1.5 text-center font-display text-xs font-extrabold uppercase tracking-widest text-white">{L("Sistem Modul PMM", "PMM Module System")}</p>
            <div className="rounded-xl bg-white p-2">
              <img src={IMG("modul-tahap.jpg")} alt={L("Modul PMM Tahap 1 dan Tahap 2", "PMM Module Level 1 and Level 2")} className="h-auto w-full rounded-lg transition-transform duration-500 hover:scale-105" loading="lazy" />
              <p className="mt-2 text-center font-display text-[11px] font-extrabold uppercase leading-tight text-navy-900">{L("Modul Yang Terbukti Dan Berkesan", "Proven & Effective Modules")}</p>
            </div>
          </div>
        </div>

        {/* ===== Books + slogan ===== */}
        <div className="flex items-end justify-between gap-3">
          <div className="w-40 space-y-1 sm:w-48" data-testid="fizikal-books">
            {[{ t: L("Baca", "Read"), c: "bg-yellow-400 text-navy-950 w-[92%]" }, { t: L("Fikir", "Think"), c: "bg-navy-600 text-white w-full" }, { t: L("Berjaya", "Succeed"), c: "bg-orange-600 text-white w-[96%]" }].map((b) => (
              <div key={b.t} className={`flex items-center justify-center gap-1 rounded-r-md rounded-l-sm py-1.5 font-display text-sm font-extrabold uppercase shadow-md ${b.c}`}>
                {b.t}{b.t === L("Berjaya", "Succeed") && <Star className="h-3.5 w-3.5 fill-yellow-300 text-yellow-300" />}
              </div>
            ))}
          </div>
          <p className="-rotate-6 text-right font-script text-2xl leading-tight text-orange-600">{L("Pendidikan Berkualiti,", "Quality Education,")}<br />{L("Generasi Hebat!", "A Great Generation!")}</p>
        </div>

        {/* ===== Branches ===== */}
        <div data-testid="fizikal-branches">
          <div className="mb-3 flex items-center justify-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-orange-500 text-white shadow"><School className="h-4 w-4" /></span>
            <span className="font-display text-xs font-extrabold uppercase tracking-widest text-navy-900 sm:text-sm">{L("Cawangan Kelas Fizikal PMM", "PMM Physical Class Branches")}</span>
          </div>
          <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2" data-testid="fizikal-branch-strip">
            {branches.map((b, i) => (
              <button type="button" key={b.loc} onClick={() => setLightbox(b.img)} className="group w-36 shrink-0 snap-start overflow-hidden rounded-xl border-2 border-orange-200 shadow-md transition-shadow duration-300 hover:shadow-lg" data-testid={`fizikal-branch-poster-${i + 1}`}>
                <img src={b.img} alt={`${L("Alamat cawangan PMM", "PMM branch address")} ${b.loc}`} className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <span className="flex items-center justify-center gap-1 bg-orange-500 px-1 py-1 text-[10px] font-bold uppercase leading-tight text-white"><MapPin className="h-3 w-3" />{b.loc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ===== Activity gallery ===== */}
        <div>
          <p className="mb-2 text-center font-display text-xs font-bold uppercase tracking-widest text-orange-600">{L("Aktiviti Kelas Fizikal PMM", "PMM Physical Class Activities")}</p>
          <div className="grid grid-cols-2 gap-2" data-testid="fizikal-gallery">
            {photos.map((src, i) => (
              <button type="button" key={src} onClick={() => setLightbox(src)} className={`group block w-full overflow-hidden rounded-xl ${i === 0 ? "col-span-2" : ""}`} data-testid={`fizikal-gallery-img-${i}`}>
                <img src={src} alt={`${L("Aktiviti kelas fizikal PMM", "PMM physical class activity")} ${i + 1}`} className={`w-full object-cover transition-transform duration-500 group-hover:scale-110 ${i === 0 ? "h-56" : "h-32"}`} loading="lazy" />
              </button>
            ))}
          </div>
        </div>

        <Link to="/register" className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-yellow-300 to-yellow-500 px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wide text-navy-950 shadow-lg transition-transform duration-300 hover:scale-[1.03]" data-testid="fizikal-register-cta">
          {L("Daftar Kelas Fizikal Sekarang", "Register For Physical Class")} <ArrowRight className="h-4 w-4" />
        </Link>

        <ManagerProfileCard
          ms={ms}
          manager={managers.fizikal}
          onOpen={setLightbox}
          photo="/landing/pmm/shafie.webp"
          poster="/landing/pmm/poster-shafie.jpg"
          fullName={["Shafie Bin Jais"]}
          role={{ ms: "Pengurus", en: "Manager" }}
          title={L("Pengurus Kelas Fizikal PMM", "PMM Physical Class Manager")}
          subtitle={L("Info detail kelas, yuran & lokasi — WhatsApp pengurus PMM", "Class details, fees & locations — WhatsApp the PMM manager")}
          testId="fizikal-manager"
          accent="red"
        />
        <ManagerBlock title={L("Pengurus Kelas PMM Sabah", "PMM Sabah Class Manager")} poster={sabahPoster} alt={L("Pengurus Kelas PMM Sabah - Cikgu Imran", "PMM Sabah Class Manager - Cikgu Imran")} manager={managers.sabah} setLightbox={setLightbox} testId="sabah-manager" />
      </div>

      {/* ===== Process strip ===== */}
      <div className="bg-gradient-to-r from-navy-950 via-navy-900 to-navy-950 px-4 py-4" data-testid="fizikal-process">
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          {PROCESS.map((p) => (
            <span key={p.en} className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wide text-white sm:text-xs">
              <span className="grid h-7 w-7 place-items-center rounded-full ring-1 ring-yellow-400 text-yellow-300"><p.icon className="h-3.5 w-3.5" /></span>{ms ? p.ms : p.en}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
